import * as React from "react";

import { splitNativeProps, type NativeMetadataProps } from "./apple-native";
import { uiCursor } from "./cursor";
import { cn } from "./utils";

const filterGeneratedProps = (props: any) => {
  const clean: any = {};
  for (const key in props) {
    if (!key.startsWith("_fg")) {
      clean[key] = props[key];
    }
  }
  return clean;
};

const Input = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<"input"> &
    NativeMetadataProps & {
      surface?: "default" | "muted";
    }
>(
  ({ className, type, surface = "default", onClick, ...props }, ref) => {
    const generatedId = React.useId();
    const cleanProps = filterGeneratedProps(props);
    const { nativeProps, rest } = splitNativeProps(cleanProps);
    const isSearch = type === "search";
    const isDate = type === "date";
    const isEmail = type === "email" || rest.inputMode === "email";
    const isNumeric = rest.inputMode === "numeric" || rest.inputMode === "decimal" || type === "number";
    const suppressCorrections = isSearch || isEmail || isNumeric;
    return (
      <input
        id={rest.id ?? rest.name ?? generatedId}
        type={type}
        data-slot="input"
        data-native-role={nativeProps["data-native-role"] ?? (isSearch ? "search-field" : isDate ? "date-field" : "text-field")}
        role={isSearch ? "searchbox" : rest.role}
        enterKeyHint={isSearch ? "search" : rest.enterKeyHint}
        autoCapitalize={suppressCorrections ? "none" : rest.autoCapitalize}
        autoCorrect={suppressCorrections ? "off" : rest.autoCorrect}
        spellCheck={suppressCorrections ? false : rest.spellCheck}
        className={cn(
          "flex h-[2.25rem] w-full min-w-0 px-[0.75rem] py-[0.25rem] text-[length:var(--text-base)] font-normal bg-input-background transition-[box-shadow] outline-none border-0",
          // Platform-facing aliases keep Apple shell geometry intact while
          // letting desktop browsers use conventional rectangular controls.
          isSearch ? "rounded-[var(--ui-search-radius)]" : "rounded-[var(--ui-control-radius)]",
          // Literal colours, not var(--muted-foreground): WKWebView (the
          // macOS/iOS shells) fails to resolve custom properties inside
          // ::placeholder, collapsing placeholders to the input's text
          // colour so they read as populated values.
          "placeholder:text-[rgba(60,60,67,0.6)] dark:placeholder:text-[rgba(235,235,245,0.6)]",
          "shadow-[var(--ui-field-shadow)]",
          // Keep keyboard focus legible without the bright outer halo.
          "focus-visible:shadow-[var(--ui-field-shadow)] focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-muted-foreground",
          `disabled:pointer-events-none disabled:opacity-50 ${uiCursor.text}`,
          "aria-invalid:shadow-[0_0_0_1px_var(--destructive),0_0_0_3.5px_color-mix(in_srgb,var(--destructive)_25%,transparent)]",
          "file:inline-flex file:h-[1.75rem] file:border-0 file:bg-transparent file:text-[length:var(--text-menu)] file:font-[var(--font-weight-medium)] file:text-foreground",
          surface === "muted" &&
            "bg-[color:color-mix(in_srgb,var(--muted)_88%,white_12%)] hover:bg-[color:color-mix(in_srgb,var(--muted)_92%,white_8%)]",
          isDate && [
            "cursor-pointer appearance-none",
            "[&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:m-0",
            "[&::-webkit-calendar-picker-indicator]:w-0 [&::-webkit-calendar-picker-indicator]:p-0 [&::-webkit-calendar-picker-indicator]:opacity-0",
            "[&::-webkit-calendar-picker-indicator]:appearance-none",
          ],
          className,
        )}
        ref={ref}
        onClick={(event) => {
          onClick?.(event);
          if (isDate && !event.defaultPrevented && !rest.disabled && !rest.readOnly) {
            event.currentTarget.showPicker?.();
          }
        }}
        {...nativeProps}
        {...rest}
      />
    );
  }
);

Input.displayName = "Input";

export { Input };
